import React, { FunctionComponent, useCallback, useMemo, useState } from 'react';
import { AdminInvestmentDocument, AdminInvestmentDocumentCreationDocumentTypeEnum } from 'src/api';
import DocumentModal from 'src/libraries/document-modal';
import Button from 'src/ui/atoms/button';
import Grid, { Col } from 'src/ui/atoms/grid';
import PlaceholderFallback from 'src/ui/atoms/placeholder-fallback';
import Spacer from 'src/ui/atoms/spacer';
import { snakeToCamelCaseWithNumbers } from 'src/ui/helper/case-transforms';
import ActionButtons from 'src/ui/molecules/action-buttons';
import Translate from 'ui/atoms/translate';
import BoxedContent from 'ui/molecules/boxed-content';
import EditInvestmentDocumentsModal from '../edit-investment-documents-modal';

interface InvestmentDocumentsListProps {
  documents: (AdminInvestmentDocument & { distributionPlatformName: string; distributionPlatformId: string })[];
  onInvestmentDocumentsEdited: () => void;
}

interface InvestmentDocumentsGrouped {
  distributionPlatformId: string;
  distributionPlatformName: string;
  documents: (AdminInvestmentDocument & { distributionPlatformName: string; distributionPlatformId: string })[];
}

const InvestmentDocumentsList: FunctionComponent<InvestmentDocumentsListProps> = ({
  documents,
  onInvestmentDocumentsEdited,
}) => {
  const [distributionPlatformEditDocumentsModalOpen, setDistributionPlatformEditDocumentsModalOpen] = useState('');

  // group documents using distribution platform field
  const groupedDocuments = useMemo(() => {
    if (!documents?.length) return [];
    const groupedMap = documents.reduce((acc, currentItem) => {
      const { distributionPlatformId, distributionPlatformName } = currentItem;

      if (!acc.has(distributionPlatformId)) {
        acc.set(distributionPlatformId, {
          distributionPlatformName: distributionPlatformName,
          distributionPlatformId: distributionPlatformId,
          documents: [],
        });
      }

      acc.get(distributionPlatformId).documents.push(currentItem);
      return acc;
    }, new Map());

    return Array.from(groupedMap.values());
  }, [JSON.stringify(documents)]);

  const onSuccessInvestmentDocumentsEdited = useCallback(() => {
    setDistributionPlatformEditDocumentsModalOpen('');
    onInvestmentDocumentsEdited();
  }, []);

  if (!documents?.length) return <Translate name="investmentDocuments.emptyView" />;

  return (
    <>
      {groupedDocuments.map((documentGroup: InvestmentDocumentsGrouped) => {
        return (
          <BoxedContent releaseSpaceWhenTitleIsEmpty={true}>
            <Grid>
              <Col width={6}>
                <Translate name={'addInvestmentDocuments.fields.distributionPlatform.label'} />
                <Spacer y={2} />
              </Col>
              <Col width={6}>
                <PlaceholderFallback>{documentGroup.distributionPlatformName}</PlaceholderFallback>
              </Col>
            </Grid>
            {documentGroup.documents.map((document, entryIndex) => (
              <Grid key={entryIndex}>
                <Col width={6}>
                  <Translate name={`investmentDocumentTypes.${snakeToCamelCaseWithNumbers(document.documentType)}`} />
                  <Spacer y={2} />
                </Col>
                <Col width={6}>
                  <Translate
                    name="common.viewDocument"
                    args={[
                      (text: string, key) => {
                        if (!document?.document) return '-';

                        return (
                          <DocumentModal key={key} {...document?.document}>
                            {text}
                          </DocumentModal>
                        );
                      },
                    ]}
                  />
                </Col>
              </Grid>
            ))}
            <ActionButtons>
              <Button
                variant="secondary"
                onClick={() => setDistributionPlatformEditDocumentsModalOpen(documentGroup.distributionPlatformId)}
              >
                <Translate name="common.edit" />
              </Button>
            </ActionButtons>
            {distributionPlatformEditDocumentsModalOpen === documentGroup.distributionPlatformId && (
              <EditInvestmentDocumentsModal
                distributionPlatformName={documentGroup.distributionPlatformName}
                distributionPlatformId={documentGroup.distributionPlatformId}
                documents={documentGroup.documents.filter((document) =>
                  Object.values(AdminInvestmentDocumentCreationDocumentTypeEnum).includes(
                    document.documentType as AdminInvestmentDocumentCreationDocumentTypeEnum,
                  ),
                )}
                onClose={() => setDistributionPlatformEditDocumentsModalOpen('')}
                onInvestmentDocumentsEdited={onSuccessInvestmentDocumentsEdited}
              />
            )}
          </BoxedContent>
        );
      })}
    </>
  );
};

export default InvestmentDocumentsList;
